<template>
    <div id="print-queue-container">
        <card title="Call Log">
            <template #header-right>
                <b-button v-if="canManageQuestionSets" variant="secondary" size="sm" @click="showQuestionSetsModal = true">
                    <i class="fas fa-cog"></i> Manage Audit Forms
                </b-button>
            </template>
            <b-form class="filter-grid mb-3" @submit.prevent="searchCalls">
                <b-row>
                    <b-col>
                        <SearchComponent v-model="userFilter" label="Agent" :option-label="userLabel"
                            search-u-r-l="/api/Call/GetAllAgents" prepopulate clearable />
                    </b-col>
                    <b-col>
                        <fg-input v-model="externalNumber" label="External Number:" />
                    </b-col>
                    <b-col>
                        <fg-input v-model="recordingSidFilter" label="Recording SID:" />
                    </b-col>
                    <b-col>
                        <SearchComponent v-model="patientFilter" label="Patient" :option-label="patientLabel"
                            search-u-r-l="/Patient" clearable />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>

                        <div>
                            <label>Call Date:</label>
                            <div class="d-flex" style="gap: 15px;">
                                <el-date-picker size="sm" v-model="startDate" today-button reset-button
                                    format="MM/dd/yyyy" type="date" />
                                <el-date-picker size="sm" v-model="endDate" today-button reset-button
                                    format="MM/dd/yyyy" type="date" />
                            </div>
                        </div>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-button type="submit" variant="primary">Search</b-button>
                    </b-col>
                </b-row>
            </b-form>
        </card>
        <card>
            <b-table id="CallLog" striped responsive="true" sort-icon-left :items="calls" :fields="fields">
                <template #cell(expandToggle)="data">
                    <div @click="selectRecording(data)" style="cursor: pointer; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;">
                        <i :class="{ 'fa fa-fw fa-chevron-right text-primary': true, 'fa-rotate-90': data.detailsShowing }"
                            style="transition: transform .15s;"></i>
                    </div>
                </template>
                <template #cell(patient)="data">
                    <router-link v-if="data.item.patientId && (data.item.patientFirstName || data.item.patientLastName)"
                        :to="`/processing/patient-dashboard/${data.item.patientId}`">
                        {{ data.item.patientFirstName }} {{ data.item.patientLastName }}
                    </router-link>
                </template>
                <template #cell(auditActions)="data">
                    <div class="d-flex gap-2">
                        <b-button v-if="canCompleteAudits && !data.item.auditCompletedAt"
                            variant="success" size="sm" @click="startAudit(data.item)">
                            <i class="fas fa-clipboard-check"></i> Audit
                        </b-button>
                        <b-button v-if="canViewAudits && data.item.auditCompletedAt"
                            variant="info" size="sm" @click="viewAudit(data.item)">
                            <i class="fas fa-eye"></i> View Audit
                        </b-button>
                    </div>
                </template>
                <template #row-details="row">
                    <card plain class="bg-white">
                        <div class="mb-3">
                            <strong>Recording SID:</strong> {{ row.item.recordingSid }}
                        </div>
                        <h6 style="font-weight: 500">Transcription</h6>
                        <b-alert v-if="downloadErrors[row.item.recordingSid]" variant="danger" show dismissible
                            @dismissed="delete downloadErrors[row.item.recordingSid]" class="py-2 mb-2">
                            Failed to load audio.
                            <a href="#" @click.prevent="loadAudioUrl(row.item.recordingSid)" class="alert-link">Retry</a>
                        </b-alert>
                        <audio :id="`audio-${row.item.recordingSid}`" controls hidden>
                            <source :id="`source-${row.item.recordingSid}`" type="audio/wav" src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=" />
                        </audio>
                        <transcription-viewer
                            entityType="0"
                            :entityId="row.item.id"
                            :currentTime="getCurrentTimeForCall(row.item.recordingSid)"
                            :compact="transcriptCompactMode"
                            @transcriptclick="transcriptClicked" />
                    </card>
                </template>
            </b-table>

            <BottomPagerBar hide-pager no-stick item-name="call" :total-rows="calls.length" show-total>
                <template #left>
                    <b-dropdown
                        :text="transcriptCompactMode ? 'Compact Transcript View' : 'Expanded Transcript View'"
                        variant="primary"
                        size="sm"
                        dropup>
                        <b-dropdown-item
                            :active="!transcriptCompactMode"
                            @click="setTranscriptViewMode(false)">
                            <i class="fas fa-list mr-2"></i>
                            Expanded View (with timestamps)
                        </b-dropdown-item>
                        <b-dropdown-item
                            :active="transcriptCompactMode"
                            @click="setTranscriptViewMode(true)">
                            <i class="fas fa-align-left mr-2"></i>
                            Compact View (text only)
                        </b-dropdown-item>
                    </b-dropdown>
                </template>
            </BottomPagerBar>
        </card>

        <!-- Audit Modal -->
        <CallLogAuditModal v-model="showAuditModal" :call="selectedCall" @audit-submitted="onAuditSubmitted" />

        <!-- Audit Viewer Modal -->
        <CallLogAuditViewer v-model="showAuditViewerModal" :call="selectedCall" />

        <!-- Question Sets Management Modal -->
        <b-modal v-model="showQuestionSetsModal" title="Manage Call Log Audit Forms" size="xl" scrollable hide-footer>
            <TaskQuestionSets :entity-type="callLogEntityType" />
        </b-modal>
    </div>
</template>

<script lang="ts">
import {Component, Vue} from 'vue-property-decorator';
import moment from 'moment';
import SearchComponent from '@/components/SearchComponent.vue';
import {DatePicker} from "element-ui";
import Axios, {AxiosRequestConfig} from "axios";
import {Call} from '@/models/Call';
import {Patient} from '@/models/Patient';
import TranscriptionViewer from '@/components/TranscriptionViewer.vue';
import {NotificationOptions} from '@/util/NotificationOptionsPresets';
import CallLogAuditModal from '@/components/CallLog/CallLogAuditModal.vue';
import CallLogAuditViewer from '@/components/CallLog/CallLogAuditViewer.vue';
import TaskQuestionSets from '@/components/Tasks/TaskQuestionSets.vue';
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import { EntityType } from '@/models/EntityType';

@Component({
    name: "CallLog",
    components: {
        SearchComponent,
        [DatePicker.name]: DatePicker,
        TranscriptionViewer,
        CallLogAuditModal,
        CallLogAuditViewer,
        TaskQuestionSets,
        BottomPagerBar,
    }
})
export default class CallLogPage extends Vue {
    protected startDate?: string = moment().add(-30, 'day').toISOString();
    protected endDate?: string = moment().toISOString();
    protected externalNumber: string = "";
    protected recordingSidFilter: string = "";
    protected patientFilter: Patient | null = null;
    protected isLoading?: boolean = false;
    protected userFilter: Call = new Call();
    protected calls: Array<Call> = [];
    protected fields: any[] = [
        { key: "expandToggle", label: "" },
        { key: "recordingSid", label: "Recording SID", sortable: true },
        { key: "externalNumber", label: "External Number", sortable: true },
        { key: "agent", label: "Agent", sortable: true },
        { key: "patient", label: "Patient", sortable: true },
        { key: "initiated", label: "Initiated", sortable: true, formatter: (d: Date) => moment.utc(d).local().format("L LT") },
        { key: "duration", label: "Duration", sortable: true },
        { key: "outbound", label: "Direction", sortable: true, formatter: (d: boolean) => d ? "Outbound" : "Inbound" },
        { key: "auditActions", label: "Audit", class: "audit-actions" },
    ];

    // Audit functionality properties
    protected selectedCall: Call | null = null;
    protected showAuditModal = false;
    protected showAuditViewerModal = false;
    protected showQuestionSetsModal = false;
    protected callLogEntityType = EntityType.CallLog;
    protected transcriptCompactMode: boolean = false;

    // Audio playback tracking for transcript highlighting
    protected currentPlaybackTime: number = 0;
    protected activeRecordingSid: string | null = null;

    // Audio URL state tracking
    protected audioUrls: { [key: string]: string } = {};
    protected audioUrlExpiry: { [key: string]: number } = {};
    protected loadingAudioUrls: { [key: string]: boolean } = {};
    protected downloadErrors: { [key: string]: string } = {};

    userLabel(c:any) {
        return c.agent ? `${c.agent}` : '';
    }

    patientLabel(p:any) {
        return p.id ? `ID: ${ p.id } ${ p.lastName }, ${ p.firstName }` : ''
    }

    mounted() {
        // Load transcript view preference from localStorage
        const savedMode = localStorage.getItem('transcriptViewMode');
        this.transcriptCompactMode = savedMode === 'compact';

        const phone = this.$route.query["phone"] as string;
        if (phone) {
            this.externalNumber = phone.replace("+", "").trim();
            this.searchCalls();
        }
    }

    async searchCalls() {
        try {
            const response = await Axios.get('/api/Call', {
                params: {
                    startDate: this.startDate,
                    endDate: this.endDate,
                    externalNumber: this.externalNumber,
                    agent: this.userFilter?.agent,
                    patientId: this.patientFilter?.id || null,
                    recordingSid: this.recordingSidFilter || null
                }
            });
            this.calls = response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    setTranscriptViewMode(compact: boolean) {
        this.transcriptCompactMode = compact;
        localStorage.setItem('transcriptViewMode', compact ? 'compact' : 'expanded');
    }

    selectRecording(r: any) {
        const wasShowing = r.detailsShowing;
        r.toggleDetails();

        // Load audio URL when expanding
        if (!wasShowing) {
            this.$nextTick(() => {
                this.loadAudioUrl(r.item.recordingSid);
            });
        }
    }

    async loadAudioUrl(itemId: string, forceRefresh: boolean = false) {
        // Prevent duplicate requests
        if (this.loadingAudioUrls[itemId]) {
            return;
        }

        // Check if URL is expired (use 25 min as safety buffer for 30 min expiry)
        const isExpired = this.audioUrlExpiry[itemId] && Date.now() > this.audioUrlExpiry[itemId];

        // Already loaded - reuse URL if not expired (b-table recreates DOM)
        if (this.audioUrls[itemId] && !isExpired && !forceRefresh) {
            const audioItem = document.getElementById(`audio-${itemId}`) as HTMLAudioElement;
            const sourceItem = document.getElementById(`source-${itemId}`) as HTMLSourceElement;

            if (audioItem && sourceItem) {
                sourceItem.src = this.audioUrls[itemId];

                // Reattach event listeners (destroyed with old DOM)
                this.attachAudioEventListeners(audioItem, itemId);

                audioItem.hidden = false;
                audioItem.load();
            }
            return;
        }

        // Clear expired or force-refreshed URL
        if (isExpired || forceRefresh) {
            delete this.audioUrls[itemId];
            delete this.audioUrlExpiry[itemId];
        }

        this.loadingAudioUrls[itemId] = true;
        delete this.downloadErrors[itemId]; // Clear any previous errors

        try {
            const response = await Axios.get(`/Call/GetAudioUrl/${itemId}`);
            const url = response.data.url;

            // Store presigned URL and expiry time (25 minutes = 1500000 ms)
            this.audioUrls[itemId] = url;
            this.audioUrlExpiry[itemId] = Date.now() + 1500000;

            const sourceItem = document.getElementById(`source-${itemId}`) as HTMLSourceElement;
            sourceItem.src = url;
            const audioItem = document.getElementById(`audio-${itemId}`) as HTMLAudioElement;

            // Attach event listeners for transcript highlighting
            this.attachAudioEventListeners(audioItem, itemId);

            audioItem.hidden = false;
            audioItem.pause();
            audioItem.load();
        } catch (err: any) {
            this.$set(this.downloadErrors, itemId, 'Failed to load audio');
        } finally {
            this.$set(this.loadingAudioUrls, itemId, false);
        }
    }

    private attachAudioEventListeners(audioItem: HTMLAudioElement, itemId: string) {
        audioItem.addEventListener('timeupdate', () => {
            if (this.activeRecordingSid === itemId) {
                this.currentPlaybackTime = audioItem.currentTime;
            }
        });

        audioItem.addEventListener('play', () => {
            this.activeRecordingSid = itemId;
            this.currentPlaybackTime = audioItem.currentTime;
        });

        audioItem.addEventListener('pause', () => {
            if (this.activeRecordingSid === itemId) {
                this.activeRecordingSid = null;
                this.currentPlaybackTime = 0;
            }
        });

        audioItem.addEventListener('ended', () => {
            if (this.activeRecordingSid === itemId) {
                this.activeRecordingSid = null;
                this.currentPlaybackTime = 0;
            }
        });

        // Handle audio load errors (e.g., expired presigned URL)
        audioItem.addEventListener('error', async () => {
            // Only auto-retry once to avoid infinite loops
            if (!this.downloadErrors[itemId]) {
                console.log(`Audio load error for ${itemId}, refreshing URL...`);
                await this.loadAudioUrl(itemId, true);
            }
        });
    }


    transcriptClicked(voicemailId: number, startTime: any) {
        const timespan = moment.duration(startTime);
        const matchedCalls = this.calls.filter(c=>c.id == voicemailId);
        if (matchedCalls.length == 0) {
            console.error(`No call found for voicemail ${voicemailId}`);
            return;
        }

        const itemId = matchedCalls[0].recordingSid;
        const audioItem = document.getElementById(`audio-${itemId}`) as HTMLAudioElement;

        if (audioItem.getAttribute('hidden')) {
            this.loadAudioUrl(itemId);
        }

        audioItem.currentTime = timespan.asSeconds();

        // If audio is not currently playing, start playing it
        if (audioItem.paused) {
            audioItem.play();
        }
    }

    getCurrentTimeForCall(recordingSid: string): number | null {
        return this.activeRecordingSid === recordingSid ? this.currentPlaybackTime : null;
    }

    // Computed properties for permissions
    get canManageQuestionSets(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === 'Admin/Call Log Question Sets');
    }

    get canCompleteAudits(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === 'Reports/Call Log Audits');
    }

    get canViewAudits(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === 'Reports/View Call Log Audits');
    }

    // Audit functionality methods

    startAudit(call: Call) {
        this.selectedCall = call;
        this.showAuditModal = true;
    }

    viewAudit(call: Call) {
        this.selectedCall = call;
        this.showAuditViewerModal = true;
    }

    async onAuditSubmitted(callId: number) {
        // Update the call object with audit completion
        const call = this.calls.find(c => c.id === callId);
        if (call) {
            call.auditCompletedAt = new Date();
            this.$forceUpdate(); // Force reactivity update
        }
    }

}
</script>

<style scoped>
::v-deep(.audit-actions) {
    width: 150px;
    text-align: center;
}

.d-flex.gap-2 {
    gap: 0.5rem;
}

/* Active dropdown item styling */
::v-deep .dropdown-menu .dropdown-item.active,
::v-deep .dropdown-menu .dropdown-item.active:hover,
::v-deep .dropdown-menu .dropdown-item.active:focus {
    background-color: #007bff !important;
    color: white !important;
    font-weight: bold !important;
}

::v-deep .dropdown-menu .dropdown-item.active i,
::v-deep .dropdown-menu .dropdown-item.active:hover i,
::v-deep .dropdown-menu .dropdown-item.active:focus i {
    color: white !important;
}

::v-deep .dropdown-menu .dropdown-item.active *,
::v-deep .dropdown-menu .dropdown-item.active:hover *,
::v-deep .dropdown-menu .dropdown-item.active:focus * {
    color: white !important;
}
</style>
